import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSocket } from '../hooks/useSocket';
import { useAuth } from '../hooks/useAuth';
import { seriesApi, evaluationApi, exerciseApi, iaApi, workoutApi } from '../services/api';

function prettifyEngineName(name) {
  if (!name) return 'Mistral';
  const s = String(name).toLowerCase();
  if (s === 'mistral') return 'Mistral';
  if (s === 'groq') return 'Groq';
  const raw = String(name).trim();
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'Mistral';
}

const ACTIVE_WORKOUT_KEY = 'iron_active_workout_id';

function loadActiveWorkoutId(userId) {
  try {
    const raw = sessionStorage.getItem(ACTIVE_WORKOUT_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (String(o.userId) !== String(userId)) return null;
    return o.workoutId != null ? String(o.workoutId) : null;
  } catch {
    return null;
  }
}

function saveActiveWorkoutId(userId, workoutId) {
  sessionStorage.setItem(
    ACTIVE_WORKOUT_KEY,
    JSON.stringify({ userId: String(userId), workoutId: workoutId != null ? String(workoutId) : '' })
  );
}

const REST_PRESETS = [60, 90, 120, 180];

const WorkoutPage = () => {
  const { user } = useAuth();
  const { iaFeedback, clearFeedback } = useSocket();

  const [workouts, setWorkouts] = useState([]);
  const [activeWorkoutId, setActiveWorkoutId] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [formData, setFormData] = useState({
    load: '',
    repetitions: '',
    plannedRestTime: 120,
    equipment: 'Barra',
  });
  const [evaluationData, setEvaluationData] = useState({
    effort: null,
    mood: null,
    sensation: null,
    feedbackText: '',
  });
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [lastSeries, setLastSeries] = useState(null);
  const [sessionSeries, setSessionSeries] = useState([]);
  const [historySeries, setHistorySeries] = useState([]);
  const [recentSeries, setRecentSeries] = useState([]);
  const [restTime, setRestTime] = useState(0);
  const [restDuration, setRestDuration] = useState(120);
  const [isResting, setIsResting] = useState(false);
  const [iaAwaitingFeedback, setIaAwaitingFeedback] = useState(false);
  const [preferredEngineLabel, setPreferredEngineLabel] = useState('Mistral');
  const [evaluationSubmitting, setEvaluationSubmitting] = useState(false);
  const [sessionElapsed, setSessionElapsed] = useState(0);
  const [sessionRunning, setSessionRunning] = useState(false);
  const [showSetModal, setShowSetModal] = useState(false);

  const activeWorkout = useMemo(
    () => workouts.find((w) => String(w.id) === String(activeWorkoutId)) || null,
    [workouts, activeWorkoutId]
  );

  const exercisesForWorkout = useMemo(() => {
    if (activeWorkout?.exercises?.length) return activeWorkout.exercises;
    return exercises;
  }, [activeWorkout, exercises]);

  const refreshWorkouts = useCallback(async () => {
    await workoutApi.ensureDefaults().catch(() => {});
    const res = await workoutApi.getAll();
    const list = Array.isArray(res.data) ? res.data : [];
    setWorkouts(list);
    return list;
  }, []);

  const refreshSessionSeries = useCallback(
    async (wid) => {
      if (!wid) {
        setSessionSeries([]);
        return;
      }
      try {
        const response = await seriesApi.getAll({ workoutId: wid, limit: 200 });
        const payload = response?.data;
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
          ? payload.data
          : [];
        setSessionSeries(list);
      } catch {
        setSessionSeries([]);
      }
    },
    []
  );

  useEffect(() => {
    let tick;
    if (sessionRunning) {
      tick = setInterval(() => setSessionElapsed((t) => t + 1), 1000);
    }
    return () => clearInterval(tick);
  }, [sessionRunning]);

  useEffect(() => {
    const boot = async () => {
      try {
        const [exRes, list, histRes] = await Promise.all([
          exerciseApi.getAll(),
          refreshWorkouts(),
          seriesApi.getAll({ limit: 120 }),
        ]);
        setExercises(Array.isArray(exRes.data) ? exRes.data : []);

        const hpayload = histRes?.data;
        const hist = Array.isArray(hpayload)
          ? hpayload
          : Array.isArray(hpayload?.data)
          ? hpayload.data
          : [];
        setHistorySeries(hist);

        const stored = user?.id ? loadActiveWorkoutId(user.id) : null;
        let pick =
          stored && list.some((w) => String(w.id) === String(stored))
            ? stored
            : list.find((w) => w.slug === 'push')?.id || list[0]?.id || null;
        if (pick) {
          setActiveWorkoutId(String(pick));
          if (user?.id) saveActiveWorkoutId(user.id, pick);
          await refreshSessionSeries(pick);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    if (user?.id) boot();
  }, [user?.id, refreshWorkouts, refreshSessionSeries]);

  useEffect(() => {
    if (!activeWorkoutId) return;
    refreshSessionSeries(activeWorkoutId);
    if (user?.id) saveActiveWorkoutId(user.id, activeWorkoutId);
  }, [activeWorkoutId, refreshSessionSeries, user?.id]);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await iaApi.getEngines();
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        const id = user.defaultAiEngineId;
        let match = id ? list.find((e) => e.id === id) : null;
        if (!match) {
          match =
            list.find((e) => String(e.name || '').toLowerCase() === 'mistral') || list[0];
        }
        setPreferredEngineLabel(prettifyEngineName(match?.name));
      } catch {
        if (!cancelled) setPreferredEngineLabel('Mistral');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.defaultAiEngineId]);

  useEffect(() => {
    if (iaFeedback) setIaAwaitingFeedback(false);
  }, [iaFeedback]);

  useEffect(() => {
    if (!iaAwaitingFeedback) return undefined;
    const t = setTimeout(() => setIaAwaitingFeedback(false), 120000);
    return () => clearTimeout(t);
  }, [iaAwaitingFeedback]);

  useEffect(() => {
    let timer;
    if (isResting && restTime > 0) {
      timer = setTimeout(() => setRestTime(restTime - 1), 1000);
    } else if (restTime === 0 && isResting) {
      setIsResting(false);
      if (lastSeries && !showEvaluationModal) {
        setShowEvaluationModal(true);
      }
    }
    return () => clearTimeout(timer);
  }, [restTime, isResting, lastSeries, showEvaluationModal]);

  const maxLoadByExercise = useMemo(() => {
    const m = {};
    for (const s of historySeries) {
      const id = s.exerciseId;
      const load = Number(s.load) || 0;
      if (!(id in m) || load > m[id]) m[id] = load;
    }
    return m;
  }, [historySeries]);

  const stats = useMemo(() => {
    const sets = sessionSeries.length;
    let vol = 0;
    let restSum = 0;
    let restN = 0;
    let prs = 0;
    for (const s of sessionSeries) {
      vol += (Number(s.load) || 0) * (Number(s.repetitions) || 0);
      const pr = Number(s.plannedRestTime);
      if (Number.isFinite(pr) && pr > 0) {
        restSum += pr;
        restN++;
      }
      const lid = s.exerciseId;
      const load = Number(s.load) || 0;
      const prevMax = maxLoadByExercise[lid];
      if (load > 0 && prevMax !== undefined && load > prevMax) prs++;
    }
    const avgRest = restN ? Math.round(restSum / restN) : null;
    return { sets, vol, avgRest, prs };
  }, [sessionSeries, maxLoadByExercise]);

  const sortedPills = useMemo(() => {
    const slugOrder = { push: 1, pull: 2, legs: 3 };
    const copy = [...workouts];
    copy.sort((a, b) => {
      const as = a.slug ? slugOrder[a.slug] ?? 50 : 99;
      const bs = b.slug ? slugOrder[b.slug] ?? 50 : 99;
      if (as !== bs) return as - bs;
      return (a.sortOrder ?? 999) - (b.sortOrder ?? 999);
    });
    return copy;
  }, [workouts]);

  const doneByExerciseId = useMemo(() => {
    const out = {};
    for (const s of sessionSeries) {
      const key = String(s.exerciseId);
      out[key] = (out[key] || 0) + 1;
    }
    return out;
  }, [sessionSeries]);

  const handleSubmitSeries = async (e) => {
    e.preventDefault();

    if (!selectedExercise) {
      alert('Selecione um exercício');
      return;
    }

    try {
      const seriesData = {
        exerciseId: selectedExercise.id,
        workoutId: activeWorkoutId || undefined,
        load: parseFloat(formData.load) || 0,
        repetitions: parseInt(formData.repetitions, 10) || 0,
        plannedRestTime: parseInt(formData.plannedRestTime, 10) || 120,
        equipment: formData.equipment,
      };

      const response = await seriesApi.create(seriesData);
      setLastSeries(response.data);

      const planned = parseInt(formData.plannedRestTime, 10) || 120;
      setRestDuration(planned);
      setRestTime(planned);
      setIsResting(true);

      setRecentSeries([response.data, ...recentSeries].slice(0, 12));
      setSessionSeries((prev) => [response.data, ...prev]);
      setHistorySeries((prev) => [response.data, ...prev].slice(0, 300));

      setFormData({
        load: '',
        repetitions: '',
        plannedRestTime: 120,
        equipment: 'Barra',
      });
      setShowSetModal(false);
    } catch (err) {
      console.error('Error creating series:', err);
      alert('Erro ao registrar série');
    }
  };

  const handleSubmitEvaluation = async (e) => {
    e.preventDefault();

    if (!evaluationData.effort || !evaluationData.mood) {
      alert('Por favor, avalie esforço e ânimo (1-5)');
      return;
    }

    if (!lastSeries) {
      alert('Nenhuma série registrada para avaliar');
      return;
    }

    try {
      setEvaluationSubmitting(true);
      await evaluationApi.create({
        seriesId: lastSeries.id,
        effort: evaluationData.effort,
        mood: evaluationData.mood,
        sensation: evaluationData.sensation != null ? evaluationData.sensation : undefined,
        feedbackText: evaluationData.feedbackText,
      });

      setShowEvaluationModal(false);
      setEvaluationData({ effort: null, mood: null, sensation: null, feedbackText: '' });
      clearFeedback();
      setIaAwaitingFeedback(true);
    } catch (err) {
      console.error('Error creating evaluation:', err);
      alert('Erro ao registrar avaliação');
      setIaAwaitingFeedback(false);
    } finally {
      setEvaluationSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatClock = (sec) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const equipmentOptions = ['Barra', 'Halteres', 'Máquina', 'Cabo', 'Corpo Livre', 'Outro'];

  const progressPct =
    isResting && restDuration > 0 ? Math.min(100, ((restDuration - restTime) / restDuration) * 100) : 0;

  const feedbackMarkdown =
    iaFeedback == null ? '' : typeof iaFeedback === 'string' ? iaFeedback : iaFeedback.markdown ?? '';
  const feedbackSource =
    iaFeedback != null && typeof iaFeedback === 'object' ? iaFeedback.source : null;
  const feedbackEngine =
    iaFeedback != null && typeof iaFeedback === 'object' ? iaFeedback.engine : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-iron-border border-t-iron-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-28">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-iron-border/70 pb-4">
        <div>
          <h1 className="font-display text-[2.6rem] leading-none tracking-[0.18em] text-iron-accent">IRON LOG</h1>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.28em] text-iron-muted">
            {new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
          </p>
        </div>
        <div className="rounded-xl border border-iron-border bg-iron-surface px-4 py-2 text-right font-mono text-[10px] text-iron-muted">
          <span className="block uppercase tracking-wider">Sessão</span>
          <strong className="text-iron-accent text-base block leading-tight">
            {activeWorkout?.name || '—'}
          </strong>
          <span className="text-iron-muted">{formatClock(sessionElapsed)}</span>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
        {sortedPills.map((w) => (
          <button
            key={w.id}
            type="button"
            onClick={() => {
              setActiveWorkoutId(String(w.id));
              setSelectedExercise(null);
              if (!sessionRunning) setSessionRunning(true);
            }}
            className={`flex-shrink-0 rounded-lg border px-3 py-2 font-mono text-[11px] transition-colors ${
              String(activeWorkoutId) === String(w.id)
                ? 'border-iron-accent bg-iron-accent text-black font-medium'
                : 'border-iron-border bg-iron-surface text-iron-muted hover:border-iron-accent hover:text-iron-accent'
            }`}
          >
            {w.name}
          </button>
        ))}
        <Link
          to="/workouts-manage"
          className="flex-shrink-0 rounded-lg border border-dashed border-iron-border px-3 py-2 font-mono text-[11px] text-iron-muted hover:border-iron-accent"
        >
          + Mais
        </Link>
      </div>

      <div className="grid grid-cols-4 gap-px overflow-hidden rounded-xl border border-iron-border bg-iron-border shadow-[0_12px_40px_rgba(0,0,0,0.45)]">
        <div className="bg-iron-surface px-1 py-3 text-center">
          <div className="font-display text-xl text-iron-accent">{stats.sets}</div>
          <div className="font-mono text-[8px] uppercase text-iron-muted">Sets ✓</div>
        </div>
        <div className="bg-iron-surface px-1 py-3 text-center">
          <div className="font-display text-xl text-iron-danger">{Math.round(stats.vol)}</div>
          <div className="font-mono text-[8px] uppercase text-iron-muted">Vol (kg)</div>
        </div>
        <div className="bg-iron-surface px-1 py-3 text-center">
          <div className="font-display text-xl text-iron-info">
            {stats.avgRest != null ? formatTime(stats.avgRest) : '—'}
          </div>
          <div className="font-mono text-[8px] uppercase text-iron-muted">Descanso</div>
        </div>
        <div className="bg-iron-surface px-1 py-3 text-center">
          <div className="font-display text-xl text-iron-success">{stats.prs}</div>
          <div className="font-mono text-[8px] uppercase text-iron-muted">PRs 🏆</div>
        </div>
      </div>

      {/* Timer hero */}
      <div className={`card relative overflow-hidden ${isResting ? 'border-iron-danger/50' : ''}`}>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-iron-accent via-iron-info to-transparent opacity-80" />
        <div className="mb-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-iron-muted">
          <span>⏱ Descanso</span>
          <span className={isResting ? 'text-iron-danger' : 'text-iron-accent'}>
            {isResting ? 'Contando' : 'Pronto'}
          </span>
        </div>
        <div
          className={`font-display text-[4rem] leading-none tracking-[0.15em] text-center ${
            isResting ? 'text-iron-info' : 'text-iron-muted'
          }`}
        >
          {formatTime(restTime)}
        </div>
        <div className="mt-4 flex flex-wrap gap-2 justify-center">
          {[...REST_PRESETS, 0].map((sec) => (
            <button
              key={sec}
              type="button"
              className={`rounded-lg border px-4 py-2 font-mono text-xs ${
                Number(formData.plannedRestTime) === sec && sec !== 0
                  ? 'border-iron-info bg-iron-info/10 text-iron-info'
                  : sec === 0 && Number(formData.plannedRestTime) === 0
                  ? 'border-iron-info bg-iron-info/10 text-iron-info'
                  : 'border-iron-border text-iron-muted hover:border-iron-accent'
              }`}
              onClick={() => {
                const val = sec === 0 ? 0 : sec;
                setFormData((fd) => ({ ...fd, plannedRestTime: val }));
                setRestDuration(sec === 0 ? 0 : sec);
                setRestTime(sec === 0 ? 0 : sec);
              }}
            >
              {sec === 0 ? 'Livre' : `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`}
            </button>
          ))}
        </div>
        <div className="mt-3 h-1 overflow-hidden rounded-full bg-iron-border">
          <div
            className="h-full rounded-full bg-gradient-to-r from-iron-info to-iron-accent transition-all duration-1000"
            style={{ width: `${isResting ? progressPct : 0}%` }}
          />
        </div>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            className="btn-secondary flex-1 py-3 uppercase tracking-wider"
            onClick={() => {
              setRestTime(restDuration);
              setIsResting(false);
            }}
          >
            Reset
          </button>
          <button
            type="button"
            className="btn-primary flex-1 py-3 uppercase tracking-wider"
            onClick={() => {
              const d = Number(formData.plannedRestTime) || 120;
              setRestDuration(d);
              setRestTime(d);
              setIsResting(true);
            }}
          >
            ▶ Iniciar descanso
          </button>
        </div>
        {isResting && (
          <button
            type="button"
            onClick={() => {
              setIsResting(false);
              setShowEvaluationModal(true);
            }}
            className="btn-secondary mt-4 w-full py-2"
          >
            Saltar timer
          </button>
        )}
        {!isResting && lastSeries && (
          <button
            type="button"
            onClick={() => setShowEvaluationModal(true)}
            className="btn-primary mt-4 w-full py-3"
          >
            Avaliar última série
          </button>
        )}
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between section-label">
          <span>Exercícios</span>
          <span>{Object.keys(doneByExerciseId).length}/{exercisesForWorkout.length || 0}</span>
        </div>
        <div className="space-y-2">
          {exercisesForWorkout.map((exercise, idx) => {
            const done = doneByExerciseId[String(exercise.id)] || 0;
            const isSelected = String(selectedExercise?.id) === String(exercise.id);
            return (
              <button
                key={exercise.id}
                type="button"
                onClick={() => {
                  setSelectedExercise(exercise);
                  setShowSetModal(true);
                }}
                className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
                  isSelected
                    ? 'border-iron-accent bg-iron-card shadow-[0_0_0_1px_rgba(232,255,71,0.35)]'
                    : 'border-iron-border bg-iron-card hover:border-iron-accent/60'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="font-display text-3xl text-iron-muted/70 w-10">
                    {String(idx + 1).padStart(2, '0')}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold text-iron-text text-xl">{exercise.name}</div>
                    <div className="font-mono text-[11px] uppercase tracking-wider text-iron-muted">
                      {done} set{done === 1 ? '' : 's'} feitos
                    </div>
                  </div>
                  <div className={`h-10 w-10 rounded-full border ${done > 0 ? 'border-iron-accent text-iron-accent' : 'border-iron-border text-iron-muted'} flex items-center justify-center`}>
                    {done > 0 ? '✓' : '○'}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* IA feedback */}
      <div id="ia-feedback" className="card border-iron-info/30">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="section-label">Feedback IA</h2>
            {feedbackSource === 'llm' && (
              <span className="rounded border border-iron-accent/40 bg-iron-accent/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-iron-accent">
                {feedbackEngine ? `API · ${feedbackEngine}` : 'API'}
              </span>
            )}
            {feedbackSource === 'local' && (
              <span className="rounded border border-iron-border px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-iron-muted">
                Análise local
              </span>
            )}
            {iaAwaitingFeedback && !iaFeedback && (
              <span className="rounded border border-iron-info/50 bg-iron-info/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-iron-info animate-pulse">
                Pedido enviado · {preferredEngineLabel}
              </span>
            )}
          </div>
          {iaFeedback && (
            <button type="button" onClick={clearFeedback} className="font-mono text-[10px] text-iron-muted hover:text-iron-text">
              Limpar
            </button>
          )}
        </div>
        {iaAwaitingFeedback && !iaFeedback && (
          <div className="flex flex-col items-center gap-4 py-10">
            <div className="h-11 w-11 animate-spin rounded-full border-2 border-iron-border border-t-iron-accent" aria-hidden />
            <p className="font-mono text-xs text-iron-muted">A gerar feedback…</p>
          </div>
        )}
        {iaFeedback ? (
          <div className="prose prose-invert max-w-none text-sm text-iron-text whitespace-pre-wrap">{feedbackMarkdown}</div>
        ) : (
          !iaAwaitingFeedback && (
            <p className="py-8 text-center font-mono text-xs text-iron-muted">
              Registe e avalie uma série para ver o feedback aqui.
            </p>
          )
        )}
      </div>

      {recentSeries.length > 0 && (
        <div>
          <div className="section-label mb-3 px-1">Últimas séries</div>
          <div className="space-y-2">
            {recentSeries.map((series, idx) => (
              <div
                key={series.id}
                className="rounded-xl border border-iron-border bg-iron-card px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  <span className="font-display text-2xl text-iron-muted w-8">{idx + 1}</span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-iron-text">{series.exercise?.name || '—'}</div>
                    <div className="font-mono text-[10px] text-iron-muted">
                      {series.load} kg × {series.repetitions}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showEvaluationModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <div className="card max-h-[90vh] w-full max-w-md overflow-y-auto border-iron-accent/40">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-display text-2xl tracking-wide text-iron-accent">Avaliar set</h2>
              <button
                type="button"
                onClick={() => setShowEvaluationModal(false)}
                className="text-iron-muted hover:text-iron-text"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmitEvaluation} className="space-y-6">
              <div>
                <p className="mb-3 font-mono text-[10px] uppercase tracking-wide text-iron-muted">Esforço percebido</p>
                <div className="flex justify-between gap-1">
                  {[1, 2, 3, 4, 5].map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setEvaluationData({ ...evaluationData, effort: num })}
                      className={`rating-button ${evaluationData.effort === num ? 'selected' : ''}`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-3 font-mono text-[10px] uppercase tracking-wide text-iron-muted">Ânimo</p>
                <div className="flex justify-between gap-1">
                  {[1, 2, 3, 4, 5].map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setEvaluationData({ ...evaluationData, mood: num })}
                      className={`rating-button ${evaluationData.mood === num ? 'selected' : ''}`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-3 font-mono text-[10px] uppercase tracking-wide text-iron-muted">Sensação física</p>
                <div className="flex justify-between gap-1">
                  {[1, 2, 3, 4, 5].map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setEvaluationData({ ...evaluationData, sensation: num })}
                      className={`rating-button ${evaluationData.sensation === num ? 'selected' : ''}`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-wide text-iron-muted">
                  Notas (opcional)
                </label>
                <textarea
                  value={evaluationData.feedbackText}
                  onChange={(e) => setEvaluationData({ ...evaluationData, feedbackText: e.target.value })}
                  className="input min-h-[88px] resize-none"
                  placeholder="Sensações, técnica…"
                />
              </div>

              <button
                type="submit"
                disabled={evaluationSubmitting}
                className="btn-primary w-full py-3 disabled:opacity-50 disabled:pointer-events-none"
              >
                {evaluationSubmitting ? 'A enviar…' : 'Confirmar avaliação'}
              </button>
            </form>
          </div>
        </div>
      )}

      {showSetModal && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <div className="card max-h-[90vh] w-full max-w-md overflow-y-auto border-iron-accent/40">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-display text-2xl tracking-wide text-iron-accent">Registrar set</h2>
              <button
                type="button"
                onClick={() => setShowSetModal(false)}
                className="text-iron-muted hover:text-iron-text"
              >
                ✕
              </button>
            </div>
            <p className="mb-4 font-mono text-xs text-iron-muted">{selectedExercise?.name || 'Exercício'}</p>
            <form onSubmit={handleSubmitSeries} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-wide text-iron-muted">
                    Carga (kg)
                  </label>
                  <input
                    type="number"
                    value={formData.load}
                    onChange={(e) => setFormData({ ...formData, load: e.target.value })}
                    className="input font-display text-xl"
                    placeholder="60"
                    step="0.01"
                    min="0"
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-wide text-iron-muted">
                    Reps
                  </label>
                  <input
                    type="number"
                    value={formData.repetitions}
                    onChange={(e) => setFormData({ ...formData, repetitions: e.target.value })}
                    className="input font-display text-xl"
                    placeholder="10"
                    min="1"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-wide text-iron-muted">
                    Descanso (s)
                  </label>
                  <input
                    type="number"
                    value={formData.plannedRestTime}
                    onChange={(e) => setFormData({ ...formData, plannedRestTime: e.target.value })}
                    className="input"
                    placeholder="120"
                    min="0"
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-wide text-iron-muted">
                    Equipamento
                  </label>
                  <select
                    value={formData.equipment}
                    onChange={(e) => setFormData({ ...formData, equipment: e.target.value })}
                    className="input"
                  >
                    {equipmentOptions.map((eq) => (
                      <option key={eq} value={eq}>
                        {eq}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <button type="submit" className="btn-primary w-full py-3">
                Registrar série
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="fixed bottom-4 left-0 right-0 z-40 px-5 sm:hidden">
        <div className="mx-auto flex max-w-app gap-2">
          <button
            type="button"
            className="btn-primary flex-1 py-3 font-mono text-xs uppercase tracking-wider"
            onClick={() => {
              setSessionRunning((r) => !r);
              if (!sessionRunning) setSessionElapsed(0);
            }}
          >
            ⚡ {sessionRunning ? 'Pausar sessão' : 'Iniciar sessão'}
          </button>
          <button
            type="button"
            className={`flex-1 rounded-xl border py-3 font-mono text-xs uppercase tracking-wider ${
              isResting
                ? 'border-iron-danger bg-iron-danger text-white'
                : 'border-iron-danger/60 bg-iron-danger/90 text-white'
            }`}
            onClick={() => {
              if (!isResting && restDuration > 0) {
                setRestTime(restDuration);
                setIsResting(true);
              } else {
                setIsResting(false);
              }
            }}
          >
            ⏱ Descanso
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkoutPage;
